<!--
  @component ServiceRecordList
  @description Registro unificado de transacciones prestacionales realizadas. Solo lectura.
-->
<template>
  <div class="sr-list">
    <div class="sr-list__toolbar">
      <el-input
        v-model="searchInput"
        placeholder="Buscar por TRX, afiliado o prestación..."
        prefix-icon="el-icon-search"
        clearable
        size="small"
        style="width: 280px"
        @input="onSearch"
      />
      <el-select
        v-model="filterOrigin"
        placeholder="Todos los orígenes"
        clearable
        size="small"
        style="width: 170px"
        @change="loadRecords"
      >
        <el-option label="Validación directa" value="validation" />
        <el-option label="Autorización" value="authorization" />
      </el-select>
      <el-select
        v-model="filterMonth"
        placeholder="Mes"
        clearable
        size="small"
        style="width: 120px"
        @change="loadRecords"
      >
        <el-option
          v-for="m in months"
          :key="m.value"
          :label="m.label"
          :value="m.value"
        />
      </el-select>
      <el-select
        v-model="filterYear"
        placeholder="Año"
        clearable
        size="small"
        style="width: 90px"
        @change="loadRecords"
      >
        <el-option v-for="y in years" :key="y" :label="y" :value="y" />
      </el-select>
    </div>

    <div class="sr-list__card">
      <el-table
        v-loading="isLoading"
        :data="records"
        size="small"
        border
        style="width: 100%"
      >
        <el-table-column label="TRX" width="120">
          <template slot-scope="{ row }">
            <span class="sr-list__trx">{{ row.transactionCode || "-" }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Fecha" width="130">
          <template slot-scope="{ row }">
            <span class="sr-list__date">{{
              row.performedAt | formatDate
            }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Afiliado" min-width="160">
          <template slot-scope="{ row }">
            <div class="sr-list__member-name">
              {{ row.memberSnapshot.lastName }},
              {{ row.memberSnapshot.firstName }}
            </div>
            <div class="sr-list__member-meta">
              {{ row.memberSnapshot.planName || "Sin plan" }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Prestación"
          min-width="200"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <span class="sr-list__practice-code">{{
              row.practiceSnapshot.code
            }}</span>
            <span class="sr-list__practice-name">{{
              row.practiceSnapshot.name
            }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Categoría" width="120">
          <template slot-scope="{ row }">
            <span class="sr-list__badge sr-list__badge--category">
              {{ categoryLabel(row.practiceSnapshot.category) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Prestador" min-width="140">
          <template slot-scope="{ row }">
            <span class="sr-list__provider">
              {{ providerName(row.providerSnapshot, row.provider) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Origen" width="160">
          <template slot-scope="{ row }">
            <span
              class="sr-list__badge"
              :class="originClass(row.origin, row.wasAudited)"
            >
              {{ originLabel(row.origin, row.wasAudited) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Estado" width="120">
          <template slot-scope="{ row }">
            <span class="sr-list__badge" :class="statusClass(row.status)">
              {{ statusLabel(row.status) }}
            </span>
          </template>
        </el-table-column>
      </el-table>

      <div class="sr-list__pagination">
        <el-pagination
          :current-page="currentPage"
          :page-size="limit"
          :total="total"
          layout="total, prev, pager, next"
          small
          @current-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import debounce from "debounce";
import serviceRecordService from "@/services/service-record";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

/** @component ServiceRecordList */
export default {
  name: "ServiceRecordList",

  filters: {
    /**
     * @description Formatea fecha ISO a dd/MM/yyyy HH:mm
     * @param {string} value
     * @returns {string}
     */
    formatDate(value) {
      if (!value) return "-";
      try {
        return format(parseISO(value), "dd/MM/yyyy HH:mm", { locale: es });
      } catch {
        return "-";
      }
    },
  },

  data() {
    return {
      records: [],
      isLoading: false,
      total: 0,
      currentPage: 1,
      limit: 20,
      searchInput: "",
      filterOrigin: "",
      filterMonth: new Date().getMonth() + 1,
      filterYear: new Date().getFullYear(),
      months: [
        { value: 1, label: "Enero" },
        { value: 2, label: "Febrero" },
        { value: 3, label: "Marzo" },
        { value: 4, label: "Abril" },
        { value: 5, label: "Mayo" },
        { value: 6, label: "Junio" },
        { value: 7, label: "Julio" },
        { value: 8, label: "Agosto" },
        { value: 9, label: "Septiembre" },
        { value: 10, label: "Octubre" },
        { value: 11, label: "Noviembre" },
        { value: 12, label: "Diciembre" },
      ],
      years: [2024, 2025, 2026, 2027],
    };
  },

  created() {
    this.onSearch = debounce(this.loadRecords, 350);
    this.loadRecords();
  },

  methods: {
    /** @description Carga transacciones con filtros activos */
    async loadRecords() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.limit,
          limit: this.limit,
        };
        if (this.searchInput) params.searchInput = this.searchInput;
        if (this.filterOrigin) params.origin = this.filterOrigin;
        if (this.filterMonth) params.billingMonth = this.filterMonth;
        if (this.filterYear) params.billingYear = this.filterYear;
        const result = await serviceRecordService.get(params);
        this.records = result.docs;
        this.total = result.total;
      } catch (e) {
        this.$message.error("Error al cargar las transacciones");
      } finally {
        this.isLoading = false;
      }
    },

    /** @param {number} page */
    onPageChange(page) {
      this.currentPage = page;
      this.loadRecords();
    },

    /**
     * @method providerName
     * @description Resuelve el nombre del prestador desde snapshot o populate
     * @param {Object} snapshot - providerSnapshot del ServiceRecord
     * @param {Object} provider - provider populado del ServiceRecord
     * @returns {string}
     */
    providerName(snapshot, provider) {
      if (snapshot?.firstName)
        return `${snapshot.lastName}, ${snapshot.firstName}`;
      if (provider?.firstName)
        return `${provider.lastName}, ${provider.firstName}`;
      return "—";
    },

    /**
     * @param {string} origin
     * @param {boolean} wasAudited
     * @returns {string}
     */
    originClass(origin, wasAudited) {
      if (origin === "authorization" && wasAudited)
        return "sr-list__badge--audited";
      if (origin === "authorization") return "sr-list__badge--auth";
      return "sr-list__badge--validation";
    },

    /**
     * @param {string} origin
     * @param {boolean} wasAudited
     * @returns {string}
     */
    originLabel(origin, wasAudited) {
      if (origin === "authorization" && wasAudited) return "Auth. auditada";
      if (origin === "authorization") return "Autorización";
      return "Validación directa";
    },

    /**
     * @param {string} status
     * @returns {string}
     */
    statusClass(status) {
      return (
        {
          pending_billing: "sr-list__badge--billed",
          billed: "sr-list__badge--billed",
          rejected: "sr-list__badge--rejected",
        }[status] || ""
      );
    },

    /**
     * @param {string} status
     * @returns {string}
     */
    statusLabel(status) {
      return (
        {
          pending_billing: "Lista para facturar",
          billed: "Facturada",
          rejected: "Rechazada",
        }[status] || status
      );
    },

    /**
     * @param {string} category
     * @returns {string}
     */
    categoryLabel(category) {
      return (
        {
          consultation: "Consulta",
          lab: "Laboratorio",
          imaging: "Imágenes",
          surgery: "Cirugía",
          dental: "Odontología",
          mental_health: "Salud mental",
          rehabilitation: "Rehabilitación",
          emergency: "Urgencias",
          hospitalization: "Internación",
          maternity: "Maternidad",
          medication: "Medicación",
          supply: "Insumos",
          prosthesis: "Prótesis",
          other: "Otros",
        }[category] ||
        category ||
        "-"
      );
    },
  },
};
</script>

<style scoped>
.sr-list {
  font-family: "Plus Jakarta Sans", sans-serif;
}

.sr-list__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.sr-list__card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.sr-list__trx {
  font-weight: 700;
  color: #002d5e;
  font-size: 13px;
  font-family: monospace;
}

.sr-list__date {
  font-size: 12px;
  color: #6b7294;
  white-space: nowrap;
}

.sr-list__member-name {
  font-weight: 600;
  color: #1a1f36;
  font-size: 13px;
}

.sr-list__member-meta {
  font-size: 11px;
  color: #05e5ba;
  font-weight: 600;
  margin-top: 1px;
}

.sr-list__practice-code {
  font-weight: 700;
  color: #05e5ba;
  font-size: 12px;
  margin-right: 4px;
}

.sr-list__practice-name {
  color: #1a1f36;
  font-size: 13px;
}

.sr-list__provider {
  font-size: 13px;
  color: #1a1f36;
}

.sr-list__badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.sr-list__badge--category {
  background: #eef1f6;
  color: #002d5e;
}

.sr-list__badge--validation {
  background: #e1f5ee;
  color: #085041;
}

.sr-list__badge--auth {
  background: #eeedfe;
  color: #26215c;
}

.sr-list__badge--audited {
  background: #faeeda;
  color: #412402;
}

.sr-list__badge--pending {
  background: #faeeda;
  color: #854f0b;
}

.sr-list__badge--billed {
  background: #eaf3de;
  color: #3b6d11;
}

.sr-list__badge--rejected {
  background: #fcebeb;
  color: #a32d2d;
}

.sr-list__pagination {
  padding: 12px 16px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f0f2f7;
}
</style>
